import { FormEvent, useEffect, useState } from "react";
import "./App.css";

type AccessKey = {
  id: string;
  name: string;
  prefix: string;
  level_names: string[];
  last_user_agent?: string;
  enabled: boolean;
};

type AccessLog = {
  key_id: string;
  key_name: string;
  time: string;
  ip: string;
  user_agent: string;
};

const levelOptions = [
  { value: "ads", label: "Advert" },
  { value: "payments", label: "Payments" },
];

const authErrorMessages: Record<string, string> = {
  missing_params: "Missing authorization parameters. Please try again.",
  session_expired: "Login session expired. Please try again.",
  state_mismatch: "Security check failed (state mismatch). Please try again.",
  token_exchange: "Failed to connect to Telegram. Please try again.",
  invalid_token: "Telegram authentication token is invalid.",
  not_in_chat: "You are not a member of a required Telegram chat.",
  token_failed: "Failed to generate session token. Please try again.",
};

function getTelegramInitData(): string {
  const initData = (window as any).Telegram?.WebApp?.initData;
  if (typeof initData === "string" && initData) return initData;

  if (!window.location.hash.startsWith("#")) return "";
  return new URLSearchParams(window.location.hash.slice(1)).get("tgWebAppData") || "";
}

// ─── Admin Keys Page ──────────────────────────────────────────────────────────

function AdminKeysPage() {
  const [adminPassword, setAdminPassword] = useState("");
  const [adminLoaded, setAdminLoaded] = useState(false);
  const [keys, setKeys] = useState<AccessKey[]>([]);
  const [logs, setLogs] = useState<AccessLog[]>([]);
  const [keyName, setKeyName] = useState("");
  const [selectedLevels, setSelectedLevels] = useState<string[]>(["ads"]);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadAdmin = () => {
    setError(null);
    fetch("/api/admin/access-keys", {
      headers: { "X-Admin-Password": adminPassword },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("Admin password is invalid");
        return response.json();
      })
      .then((data) => {
        setKeys(data.keys || []);
        setLogs(data.logs || []);
        setAdminLoaded(true);
      })
      .catch((err) => setError(err.message));
  };

  const createKey = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    fetch("/api/admin/access-keys", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Admin-Password": adminPassword,
      },
      body: JSON.stringify({ name: keyName, levels: selectedLevels }),
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(await response.text());
        return response.json();
      })
      .then((data) => {
        setNewSecret(data.secret);
        setKeyName("");
        loadAdmin();
      })
      .catch((err) => setError(err.message));
  };

  const disableKey = (id: string) => {
    fetch(`/api/admin/access-keys/${id}`, {
      method: "DELETE",
      headers: { "X-Admin-Password": adminPassword },
    }).then(() => loadAdmin());
  };

  const toggleLevel = (level: string) => {
    setSelectedLevels((levels) =>
      levels.includes(level)
        ? levels.filter((item) => item !== level)
        : [...levels, level],
    );
  };

  return (
    <div className="App">
      <header className="App-header">
        <img src="/logo.svg" className="App-logo" alt="logo" />
        <h1 style={{ fontSize: "1.4rem", margin: 0 }}>Access Keys Admin</h1>
        <section className="admin-panel">
          <div className="panel row">
            <input
              value={adminPassword}
              onChange={(event) => setAdminPassword(event.target.value)}
              placeholder="Admin password"
              type="password"
            />
            <button onClick={loadAdmin}>Open admin</button>
          </div>
          {adminLoaded && (
            <>
              <form className="panel" onSubmit={createKey}>
                <input
                  value={keyName}
                  onChange={(event) => setKeyName(event.target.value)}
                  placeholder="Key name"
                />
                <div className="levels">
                  {levelOptions.map((level) => (
                    <label key={level.value}>
                      <input
                        type="checkbox"
                        checked={selectedLevels.includes(level.value)}
                        onChange={() => toggleLevel(level.value)}
                      />
                      {level.label}
                    </label>
                  ))}
                </div>
                <button type="submit">Create key</button>
              </form>
              {newSecret && <code className="secret">New key: {newSecret}</code>}
              <div className="list">
                {keys.map((key) => (
                  <article key={key.id} className="card">
                    <strong>{key.name}</strong>
                    <span>
                      {key.prefix}... / {key.level_names.join(", ")}
                    </span>
                    <small>Last UA: {key.last_user_agent || "never"}</small>
                    <button disabled={!key.enabled} onClick={() => disableKey(key.id)}>
                      {key.enabled ? "Disable" : "Disabled"}
                    </button>
                  </article>
                ))}
              </div>
              <div className="logs">
                <h3>Recent logins</h3>
                {logs.map((log, index) => (
                  <p key={`${log.key_id}-${index}`}>
                    {new Date(log.time).toLocaleString()} — {log.key_name} — {log.ip} — {log.user_agent}
                  </p>
                ))}
              </div>
            </>
          )}
        </section>
        {error && <p className="error">{error}</p>}
      </header>
    </div>
  );
}

// ─── Login Page ───────────────────────────────────────────────────────────────

function LoginPage() {
  const [showKeyLogin, setShowKeyLogin] = useState(false);
  const [accessKey, setAccessKey] = useState("");
  const [keyError, setKeyError] = useState<string | null>(null);
  const [miniAppError, setMiniAppError] = useState<string | null>(null);

  const authErrorParam = new URLSearchParams(window.location.search).get("auth_error");
  const oidcError = authErrorParam ? authErrorMessages[authErrorParam] || "Authentication failed. Please try again." : null;

  useEffect(() => {
    const webApp = (window as any).Telegram?.WebApp;
    webApp?.ready?.();
    webApp?.expand?.();

    let cancelled = false;
    let requested = false;
    let attempts = 0;

    const authenticate = () => {
      const initData = getTelegramInitData();
      if (!initData || requested) return false;
      requested = true;
      setMiniAppError(null);
      fetch("/auth/telegram/miniapp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ init_data: initData }),
      })
        .then((response) => {
          if (cancelled) return;
          if (response.ok) {
            window.location.replace("/");
            return;
          }
          if (response.status === 403) {
            setMiniAppError(authErrorMessages.not_in_chat);
            return;
          }
          setMiniAppError("Telegram Mini App authentication failed.");
        })
        .catch(() => {
          if (!cancelled) setMiniAppError("Failed to connect to Telegram. Please try again.");
        });
      return true;
    };

    const interval = window.setInterval(() => {
      attempts += 1;
      if (authenticate()) {
        window.clearInterval(interval);
        return;
      }
      if (attempts >= 20) {
        window.clearInterval(interval);
        const platform = String(webApp?.platform || "");
        const hasAuthError = new URLSearchParams(window.location.search).has("auth_error");
        if (platform && platform !== "unknown" && !hasAuthError) {
          window.location.replace("/auth/telegram");
        }
      }
    }, 100);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  const loginWithAccessKey = (event: FormEvent) => {
    event.preventDefault();
    setKeyError(null);
    fetch("/auth/access-key", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key: accessKey }),
    })
      .then((response) => {
        if (response.ok) window.location.reload();
        else setKeyError("Access key is invalid or disabled.");
      })
      .catch(() => setKeyError("An error occurred. Please try again."));
  };

  return (
    <div className="App">
      <header className="App-header">
        <img src="/logo.svg" className="App-logo" alt="logo" />
        {miniAppError ? (
          <p>{miniAppError}</p>
        ) : (
          <p>To access this page, please log in with Telegram</p>
        )}
        <a href="/auth/telegram" className="telegram-login-button">
          Log in with Telegram
        </a>
        <button className="secondary-button" onClick={() => setShowKeyLogin(!showKeyLogin)}>
          Log in with access key
        </button>
        {showKeyLogin && (
          <form className="panel" onSubmit={loginWithAccessKey}>
            <input
              value={accessKey}
              onChange={(event) => setAccessKey(event.target.value)}
              placeholder="hugo_access_key"
            />
            <button type="submit">Enter</button>
          </form>
        )}
        {oidcError && <p className="error">{oidcError}</p>}
        {miniAppError && <p className="error">{miniAppError}</p>}
        {keyError && <p className="error">{keyError}</p>}
      </header>
    </div>
  );
}

// ─── Router ───────────────────────────────────────────────────────────────────

function App() {
  const path = window.location.pathname;

  if (path === "/admin-keys" || path === "/admin-keys/") {
    return <AdminKeysPage />;
  }

  return <LoginPage />;
}

export default App;
